<template>
    <div v-if="computedElement" :class="[colSpan, alignmentClass, { 'col-start-1': computedElement.new_row }]">
        <CustomInput
            v-if="computedElement.type === 'textbox' && computedElement.type_settings.input_type === 'string'"
            ref="dynamic-element"
            v-model:value="computedElement.value"
            :label="computedElement.label ?? undefined"
            :disabled="computedElement.disabled || preconditionDisabled"
            input-type="text"
            :rules="rules"
            :details="preconditionHint || details"
            :show-required-indication="computedElement.required"
            @input="onTextInput"
        ></CustomInput>

        <CustomInput
            v-else-if="computedElement.type === 'textbox' && (computedElement.type_settings.input_type === 'int' || computedElement.type_settings.input_type === 'double')"
            ref="dynamic-element"
            v-model:value="computedElement.value"
            :label="computedElement.label ?? undefined"
            :step="computedElement.type_settings.input_type === 'int' ? '1' : '0.1'"
            :disabled="computedElement.disabled || preconditionDisabled"
            input-type="number"
            :rules="rules"
            :details="preconditionHint || details"
            :show-required-indication="computedElement.required"
            @input="onTextInput"
        ></CustomInput>

        <CustomTextArea
            v-else-if="computedElement.type === 'textarea' && computedElement.type_settings.input_type === 'string'"
            ref="dynamic-element"
            v-model:value="computedElement.value"
            :label="computedElement.label ?? undefined"
            :disabled="computedElement.disabled || preconditionDisabled"
            :rules="rules"
            :details="preconditionHint || details"
            :show-required-indication="computedElement.required"
            @input="$emit('input')"
        ></CustomTextArea>

        <DistributionChartModal
            v-else-if="computedElement.type === 'chartspread' && (computedElement.type_settings.input_type === 'int' || computedElement.type_settings.input_type === 'double')"
            ref="dynamic-element"
            v-model:value="computedElement.value"
            :label="computedElement.label ?? undefined"
            :amount="preconditionValues[0]"
            :average="preconditionValues[1]"
            :min="0.05"
            :max="0.3"
            :default="0.1"
            :disabled="computedElement.disabled || preconditionDisabled"
            :details="preconditionHint || details"
            @input="$emit('input')"
        ></DistributionChartModal>

        <CustomSelect
            v-else-if="computedElement.type_settings.input_type === 'keyvaluepair' && computedElement.type === 'dropdown'"
            ref="dynamic-element"
            v-model:value="computedElement.value"
            :items="selectableValues"
            item-text="value"
            :disabled="computedElement.disabled || preconditionDisabled"
            return-object
            :rules="rules"
            item-value="id"
            required
            :allow-empty="!computedElement.required"
            :label="computedElement.label ?? undefined"
            :details="preconditionHint || details"
            :show-required-indication="computedElement.required"
            @input="onSelectInput"
        ></CustomSelect>

        <CustomMultiSelect
            v-else-if="computedElement.type_settings.input_type === 'keyvaluepairlist' && computedElement.type === 'multiselect'"
            ref="dynamic-element"
            v-model:value="computedElement.value"
            :items="computedElement.type_settings.values"
            item-text="value"
            :disabled="computedElement.disabled || preconditionDisabled"
            :rules="rules"
            item-value="id"
            required
            show-apply-button
            :label="computedElement.label ?? undefined"
            :show-required-indication="computedElement.required"
            :details="preconditionHint || details"
            @input="$emit('input')"
        ></CustomMultiSelect>

        <RadioButtonGroup
            v-else-if="computedElement.type_settings.input_type === 'keyvaluepair' && computedElement.type === 'radiobutton'"
            ref="dynamic-element"
            v-model:value="computedElement.value"
            :items="computedElement.type_settings.values"
            item-text="value"
            :disabled="computedElement.disabled || preconditionDisabled"
            return-object
            :rules="rules"
            item-value="id"
            :label="computedElement.label ?? undefined"
            :radio-button-group="`${sectionId}_${computedElement.id}`"
            :details="preconditionHint || details"
            @input="$emit('input')"
        ></RadioButtonGroup>

        <FormCheckbox
            v-else-if="computedElement.type_settings.input_type === 'bool' && computedElement.type === 'checkbox'"
            v-model:value="computedElement.value"
            :disabled="computedElement.disabled || preconditionDisabled"
            :label="computedElement.label ?? undefined"
            classes="w-full"
        ></FormCheckbox>

        <CustomRangeInput
            v-else-if="computedElement && computedElement.type === 'slider'"
            v-model:value="computedElement.value"
            :disabled="computedElement.disabled || preconditionDisabled"
            :label="computedElement.label || ''"
            :min="computedElement.type_settings.minimum_value || 0"
            :max="computedElement.type_settings.maximum_value || 100"
            :step="computedElement.type_settings.stepsize || 1"
            :unit="computedElement.type_settings.unit || ''"
            class="my-3"
        >
        </CustomRangeInput>

        <DatePicker
            v-else-if="computedElement.type_settings.input_type === 'datetime' && computedElement.type === 'datepicker'"
            ref="dynamic-element"
            v-model:value="computedDateOnlyValue"
            :required="computedElement.required"
            :min="computedElement.type_settings.minimum_value"
            :max="computedElement.type_settings.maximum_value"
            :disabled="computedElement.disabled || preconditionDisabled"
            :label="computedElement.label ?? undefined"
            :details="preconditionHint || details"
        >
        </DatePicker>

        <TimePicker
            v-else-if="computedElement.type_settings.input_type === 'datetime' && computedElement.type === 'clockpicker'"
            ref="dynamic-element"
            v-model:value="computedElement.value"
            :required="computedElement.required"
            :disabled="computedElement.disabled || preconditionDisabled"
            :label="computedElement.label ?? undefined"
            :details="preconditionHint || details"
        >
        </TimePicker>

        <DateTimePicker
            v-else-if="computedElement.type_settings.input_type === 'datetime' && computedElement.type === 'datetimepicker'"
            ref="dynamic-element"
            v-model:value="computedElement.value"
            :required="computedElement.required"
            :min="computedElement.type_settings.minimum_value"
            :max="computedElement.type_settings.maximum_value"
            :disabled="computedElement.disabled || preconditionDisabled"
            :label="computedElement.label ?? undefined"
            :details="preconditionHint || details"
        >
        </DateTimePicker>

        <DateTimeRecurrencePicker
            v-else-if="computedElement.type_settings.input_type === 'datetime_with_recurrence' && computedElement.type === 'datepicker_with_recurrence'"
            ref="dynamic-element"
            v-model:value="computedElement.value"
            :required="computedElement.required"
            :min="computedElement.type_settings.minimum_value"
            :max="computedElement.type_settings.maximum_value"
            :disabled="computedElement.disabled || preconditionDisabled"
            :label="computedElement.label ?? undefined"
            :details="preconditionHint || details"
        >
        </DateTimeRecurrencePicker>

        <div v-else-if="computedElement.type === 'button' && computedElement.type_settings.action === 'image-upload'" class="mb-2">
            <DynamicFormImages v-if="computedElement && computedElement.type_settings.values && computedElement.type_settings.values.length" v-model:element="computedElement" :assignment-id="assignmentId" />
        </div>

        <DynamicFormButton
            v-else-if="computedElement.type === 'button'"
            ref="dynamic-element"
            :element="computedElement"
            :disabled="computedElement.disabled"
            @clone-section="(cloneFormSectionOptions) => $emit('clone-section', cloneFormSectionOptions)"
            @backend-retrieval="(buttonElement) => $emit('backend-retrieval', buttonElement)"
        ></DynamicFormButton>

        <DynamicFormLink v-else-if="computedElement.type === 'linkbutton'" :element="computedElement" :disabled="computedElement.disabled"></DynamicFormLink>

        <DynamicFormQualityDescriptor v-else-if="computedElement.type === 'qualitydescriptor'" :element="computedElement" />

        <GenericChart v-else-if="computedElement.type === 'highchart'" :options="computedElement?.value?.options" class="my-3" />

        <DynamicFormAllocationSummary v-else-if="computedElement.type === 'allocation-summary'" :label="computedElement.label" :allocated="allocation?.allocated ?? 0" :total="allocation?.total ?? null" />

        <div v-else>{{ 'Label:' + computedElement.label }}</div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { parseISO } from 'date-fns';
import { FormElement } from '@/types';
import { ValidatableRule, type FieldDetails } from '@/mixins/validatableField';
import CustomInput from '@/components/ui/CustomInput.vue';
import CustomTextArea from '@/components/ui/CustomTextArea.vue';
import CustomSelect from '@/components/ui/CustomSelect.vue';
import CustomMultiSelect from '@/components/ui/CustomMultiSelect.vue';
import DatePicker from '@/components/ui/DatePicker.vue';
import TimePicker from '@/components/ui/TimePicker.vue';
import DateTimePicker from '@/components/ui/DateTimePicker.vue';
import DateTimeRecurrencePicker from '@/components/ui/DateTimeRecurrencePicker.vue';
import RadioButtonGroup from '@/components/ui/RadioButtonGroup.vue';
import FormCheckbox from '@/components/ui/FormCheckbox.vue';
import DistributionChartModal from '@/components/common/DistributionChartModal.vue';
import DynamicFormButton from '@/components/common/DynamicFormButton.vue';
import DynamicFormLink from '@/components/common/DynamicFormLink.vue';
import DynamicFormImages from '@/components/common/DynamicFormImages.vue';
import DynamicFormQualityDescriptor from '../DynamicFormQualityDescriptor.vue';
import DynamicFormAllocationSummary from '../DynamicFormAllocationSummary.vue';
import CustomRangeInput from '@/components/ui/CustomRangeInput.vue';
import GenericChart from '@/components/charts/GenericChart.vue';

/** The `type_settings.action` value that makes a field ask the backend to recompute other fields (spelled as the backend sends it). */
const BACKEND_RETRIEVAL_TRIGGER_ACTION = 'backend-retrival-trigger';

/** How long typing or picking must pause before a textbox/datepicker trigger calls the backend. */
const BACKEND_RETRIEVAL_DEBOUNCE_MS = 400;

function toPickerValue(dateOnly: string): string {
    return parseISO(dateOnly).toISOString();
}

export default defineComponent({
    components: {
        CustomInput,
        CustomTextArea,
        CustomSelect,
        CustomMultiSelect,
        DatePicker,
        TimePicker,
        DateTimePicker,
        DateTimeRecurrencePicker,
        RadioButtonGroup,
        FormCheckbox,
        DistributionChartModal,
        DynamicFormButton,
        DynamicFormLink,
        DynamicFormImages,
        DynamicFormQualityDescriptor,
        DynamicFormAllocationSummary,
        CustomRangeInput,
        GenericChart,
    },
    props: {
        element: {
            type: Object as () => null | FormElement,
            required: true,
        },
        preconditionDisabled: {
            type: Boolean,
            default: false,
        },
        preconditionData: {
            type: Array as () => { label: string | null | undefined; value: any; id: string | undefined }[],
            default: () => [],
        },
        rules: {
            type: Array as () => ValidatableRule[],
            default: () => [],
        },
        details: {
            type: Object as () => FieldDetails | undefined,
            default: undefined,
        },
        sectionId: {
            type: String,
            default: '',
        },
        assignmentId: {
            type: Number as () => number | null | undefined,
            default: null,
        },
        /** Dropdown values that cannot be picked, because they are picked in another copy of the section */
        excludedValueIds: {
            type: Array as () => string[],
            default: () => [],
        },
        allocation: {
            type: Object as () => null | { allocated: number; total: number | null },
            default: null,
        },
        /** A button in a row with a radio group whose label sits above its boxes, pushed down by that label */
        labelOffset: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:element', 'input', 'clone-section', 'backend-retrieval'],
    data() {
        return { backendRetrievalTimeout: null as null | ReturnType<typeof setTimeout> };
    },
    computed: {
        selectableValues(): any[] {
            const values = this.computedElement?.type_settings.values || [];
            if (!this.$props.excludedValueIds.length) {
                return values;
            }
            return values.filter((currentValue) => !this.$props.excludedValueIds.includes(currentValue.id));
        },
        preconditionValues(): any[] {
            return this.$props.preconditionData.map((currentPreconditionData) => currentPreconditionData.value || null);
        },
        preconditionHint(): { type: 'hint' | 'warning'; text: string } | null {
            if (this.$props.preconditionDisabled) {
                return {
                    type: 'hint',
                    text: this.$t('global.forms.steps.dynamicForm.hints.preconditions').replace(
                        '$FIELDS',
                        `"${this.$props.preconditionData.map((currenPreconditionData) => currenPreconditionData.label).join(`", "`)}"`
                    ),
                };
            }
            return null;
        },
        /* If the input type we resolve to based on our v-if has a property called 'validInput' we return it.
            Otherwise, we assume that it is valid */
        validInput(): boolean {
            const element = this.$refs['dynamic-element'] as any;
            return Boolean(!element) || element.validInput === undefined || element.validInput;
        },
        computedElement: {
            get(): null | FormElement {
                return this.$props.element;
            },
            set(newElement: null | FormElement) {
                this.$emit('update:element', newElement);
            },
        },
        computedDateOnlyValue: {
            get(): string {
                const dateOnly = this.element?.type_settings.date_only;
                return dateOnly ? toPickerValue(dateOnly) : this.computedElement?.value;
            },
            set(newValue: string) {
                if (this.element) {
                    this.element.type_settings.date_only = null;
                    this.element.value = newValue;
                    this.scheduleBackendRetrieval();
                }
            },
        },
        colSpan(): string {
            const colClasses: Record<number, string> = {
                1: 'col-span-1',
                2: 'col-span-2',
                3: 'col-span-3',
                4: 'col-span-4',
                5: 'col-span-5',
                6: 'col-span-6',
            };
            if (this.computedElement?.type === 'button' && this.computedElement?.type_settings.action === 'image-upload') {
                return colClasses[2];
            }
            return colClasses[this.computedElement?.column_width || 2] || 'col-span-2';
        },
        /**
         * A button is top-aligned in its grid cell, so it lines up with the text boxes of its row rather than being pushed down by their hint text.
         * Any element next to a labelled radio group is pushed down by the height of the group's label, to line up with its boxes.
         */
        alignmentClass(): (string | { [className: string]: boolean })[] {
            // The group's label is a line of the surrounding 1.5rem line height
            const offset = { 'pt-6': this.labelOffset };
            if (this.computedElement?.type !== 'button' || this.computedElement?.type_settings.action === 'image-upload') {
                return [offset];
            }
            return ['flex items-start min-h-[3.25rem]', offset];
        },
    },
    beforeUnmount() {
        this.cancelBackendRetrieval();
    },
    methods: {
        onTextInput() {
            this.scheduleBackendRetrieval();
            this.$emit('input');
        },
        /* Textbox and datepicker triggers fire while the reader is still typing or picking, so they are debounced; the dropdown trigger fires once per choice and stays immediate. */
        scheduleBackendRetrieval() {
            if (this.element?.type_settings.action !== BACKEND_RETRIEVAL_TRIGGER_ACTION) {
                return;
            }
            this.cancelBackendRetrieval();
            this.backendRetrievalTimeout = setTimeout(() => {
                this.backendRetrievalTimeout = null;
                this.$emit('backend-retrieval', this.$props.element);
            }, BACKEND_RETRIEVAL_DEBOUNCE_MS);
        },
        cancelBackendRetrieval() {
            if (this.backendRetrievalTimeout !== null) {
                clearTimeout(this.backendRetrievalTimeout);
                this.backendRetrievalTimeout = null;
            }
        },
        onSelectInput() {
            if (this.element?.type_settings.action === BACKEND_RETRIEVAL_TRIGGER_ACTION) {
                this.$emit('backend-retrieval', this.$props.element);
            }
            this.$emit('input');
        },
    },
});
</script>
